import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { User } from '../models/user.model';
import { MyAccountState } from '../store/state/my-account.state';
import { UserProfile } from '../models/user-profile.model';
import { Language } from '../models/language.model';
import { BillingPlatformRole } from '../models/biling-platform-role';
import {
    USER_PROFILES_ENDPOINT,
    USER_LIST_ENDPOINT,
    USER_PRODUCTS_ENDPOINT,
    USER_COUNTRIES_ENDPOINT,
    USER_ADMINISTRATORS_ENDPOINT,
    EMAIL_SUBSCRIPTIONS_ENDPOINT, USER_PERMISSION,
    BILLING_PLATFORM_ROLES_ENDPOINT,
    LEARNING_ROLES_ENDPOINT,
    EXPORT_USERS_TO_EXCEL_ENDPOINT,
} from '../app.constants';
import { UserProduct } from '../models/user-product.model';
import { Country } from '../models/country.model';
import { CountryAndLanguage } from '../models/country-and-language.model';
import { EmailSubscriptionCollection } from '../interfaces/email-subscription-collection.interface';
import { LearningRole } from '../models/learning-role';

@Injectable()
export class UserManagementService {

    public totalResults = new Subject();
    private readonly userListEndpoint = USER_LIST_ENDPOINT;
    private readonly userProfilesEndpoint = USER_PROFILES_ENDPOINT;
    private readonly userProductsEndpoint = USER_PRODUCTS_ENDPOINT;
    private readonly userProductsRequest: Observable<any>;
    private readonly billingPlatformRolesEndpoint = BILLING_PLATFORM_ROLES_ENDPOINT;
    private readonly learningRolesEndpoint = LEARNING_ROLES_ENDPOINT;  
    private readonly administratorsRequest: Observable<User[]>;
    private readonly myProfileRequest: Observable<User>;

    constructor(private http: HttpClient, private store: Store<MyAccountState>) {
        this.userProductsRequest = this.http.get(this.userProductsEndpoint);
        this.administratorsRequest = this.http.get<User[]>(USER_ADMINISTRATORS_ENDPOINT);
        this.myProfileRequest = this.http.get<User>(this.userListEndpoint + '/me');
    }

    /**
     * Returns the current user's profile from the NgRx store instead of hitting
     * `/me` again. The profile is loaded once (LoadProfile effect) at bootstrap,
     * so this avoids duplicate `/me` requests from permission/role checks.
     * Emits once the profile is populated, then completes.
     * @returns {Observable<User>} the current user's profile
     */
    getCurrentUserProfile(): Observable<User> {
        return this.store.select(state => state.profile).pipe(
            filter(profile => !!profile && Object.keys(profile).length > 0),
            take(1)
        );
    }

    get userProducts(): Observable<UserProduct[]> {
        return this.userProductsRequest as Observable<UserProduct[]>;
    }

    getUserProfiles(): Observable<UserProfile[]> {
        return this.http.get<UserProfile[]>(this.userProfilesEndpoint);
    }

    getUserProfile(id = 'me'): Observable<User> {
        return id === 'me' 
            ? this.http.get<User>(this.userListEndpoint + '/me')
            : this.http.get(this.userListEndpoint + '/' + id) as Observable<User>;
    }

    getUserList(options: {
        page?: string; size?: string; sortOrder?: string; sortOption?: string; query?: string;
    }
    ): Observable<User[]> {
        const httpParams = new HttpParams()
            .append('page', options.page || '')
            .append('query', options.query || '');
        return this.http.get<User[]>(this.userListEndpoint, {
            observe: 'response',
            params: httpParams,
        }).pipe(
            tap((response: HttpResponse<User[]>) => {
                this.totalResults.next(response.headers.get('X-Total-Count'));
            }),
            map((response: HttpResponse<User[]>) => response.body.map((user: User) => new User(user)), shareReplay(1))
        );
    }
    hasSubscriptionLevel(permitedRoles: string[]): Observable<boolean> {
        return this.getCurrentUserProfile().pipe(map(user =>
            permitedRoles.length !== 0 &&
            permitedRoles.some(level => level === user.subscriptionLevel) && 
            !user.unit4Employee));

    }

    getAdministrators(): Observable<User[]> {
        return this.administratorsRequest;
    }

    addUser(user: User): Observable<any> {
        return this.http.post(this.userListEndpoint, user);
    }

    updateUser(user: User, self?: boolean): Observable<any> {
        return this.http.put(this.userListEndpoint + (self ? '/me' : ''), user);
    }

    deleteUser(user: User): Observable<any> {
        return this.http.delete(this.userListEndpoint + '/' + user.id);
    }

    /**
     * Must go through `HttpClient` (not a plain `<a href>`) so `TokenInterceptor` attaches the
     * bearer token - this endpoint is authenticated and a top-level navigation would 401.
     * @returns {Observable<HttpResponse<Blob>>} the Excel file response, used to recover the
     * filename from `Content-Disposition` before handing the blob to the browser
     */
    exportUsersToExcel(): Observable<HttpResponse<Blob>> {
        return this.http.get(EXPORT_USERS_TO_EXCEL_ENDPOINT, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    getCountriesAndLanguages(): Observable<CountryAndLanguage> {
        return this.http.get<CountryAndLanguage>(USER_COUNTRIES_ENDPOINT).pipe(shareReplay(1));
    }

    getCountries(countriesAndLanguages: CountryAndLanguage): Country[] {
        const countries: Country[] = [];
        const countryCodes: string[] = [];

        Object.keys(countriesAndLanguages).forEach(item => {
            const itemValue = countriesAndLanguages[item];
            if (countryCodes.indexOf(itemValue.country.code) === -1) {
                countryCodes.push(itemValue.country.code);
                countries.push(itemValue.country);
            }
        });

        return countries.sort((a, b) => a.name > b.name ? 1 : -1);
    }

    getLanguagesForCountry(countriesAndLanguages: CountryAndLanguage, countryCode: string): Language[] {
        const languages: Language[] = [];
        const languageCodes: string[] = [];

        if (!countryCode) {
            return [];
        }

        Object.keys(countriesAndLanguages).filter(item => 
            item.indexOf(`_${countryCode.toUpperCase()}`) !== -1 // e.g. GB in en_GB
        ).forEach(item => {
            const language = countriesAndLanguages[item].language;
            if (languageCodes.indexOf(language.code) === -1) {
                languageCodes.push(language.code);
                languages.push(language);
            }
        });

        return languages.sort((a, b) => a.name > b.name ? 1 : -1);
    }

    hasPermission(item: USER_PERMISSION): Observable<boolean> {
        return this.getCurrentUserProfile().pipe(map(user => user.permissions.indexOf(item) !== -1));
    }

    getSubscriptions(): Observable<EmailSubscriptionCollection> {
        return this.http.get<EmailSubscriptionCollection>(EMAIL_SUBSCRIPTIONS_ENDPOINT);
    }

    updateSubscriptions(subscriptions: EmailSubscriptionCollection): Observable<any> {
        return this.http.post(EMAIL_SUBSCRIPTIONS_ENDPOINT, subscriptions);
    }

    getBillingPlatformRoles(): Observable<BillingPlatformRole[]> {
        return this.http.get<BillingPlatformRole[]>(this.billingPlatformRolesEndpoint);
    }

    getLearningRoles(): Observable<LearningRole[]> {
        return this.http.get<LearningRole[]>(this.learningRolesEndpoint, { observe: 'response' }).pipe(
            switchMap((response: HttpResponse<LearningRole[]>) => {
                // Extracts the response body to access LearningRole[]
                const roles = response.body;
                return this.getCurrentUserProfile().pipe(
                    map(user => roles.filter(role => 
                        role.applicableSubscriptionLevel.some((level: string) => level === user.subscriptionLevel)
                    ))
                );
            })
        );    
    }

}
